import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { MessageDescriptorPropType } from '../../../prop-types/intl';
import { removeItemFromSelection as removeItemFromSelectionAction } from '../../../store/bulk-errors-and-warnings/bulk-errors-and-warnings.actions';
import { goToOrderDetails } from '../../../store/orders/order-details/orders.details.actions';
import { launchShipmentDetail } from '../../../store/shipments/shipment-details/shipments.details.actions';
import { BulkErrorOrWarning } from './bulk-error-or-warning';
import style from './bulk-errors-and-warning-modal.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const messages = defineMessages({
  close: {
    description: 'bulkErrorsAndWarningsModal.close',
    defaultMessage: 'Close',
    id: 'V+c+kP',
  },
});

// item.id and errorsAndWarnings.data.item.id are both fulfillmentPlanIds
const removeItemsFromState = (selectedItems, errorsAndWarnings, items) => {
  const clonedSelectedItems = [...selectedItems];
  const idsToRemove = items.map((item) => item.id);
  const clonedErrorsAndWarnings = [...errorsAndWarnings].map((error) => {
    return {
      ...error,
      data: error.data.filter((item) => idsToRemove.indexOf(item.id) === -1),
    };
  });

  return {
    selectedItems: clonedSelectedItems.filter((item) => idsToRemove.indexOf(item.fulfillmentPlanId) === -1),
    errorsAndWarnings: clonedErrorsAndWarnings.filter((error) => error.data.length > 0),
  };
};

export const BulkErrorsAndWarningsModal = ({
  title,
  cancelButtonName = messages.close,
  errorsAndWarnings: propErrorsAndWarnings = emptyArray,
  selectedItems: propSelectedItems,
  onContinue = noop,
  hasSelectedItems: propHasSelectedItems = true,
  modalFrameworkMethods,
  dataType,
  isBatchPrinting = false,
  continueCanBeDisabled = false,
}) => {
  const dispatch = useDispatch();

  const [errorsAndWarnings, setErrorsAndWarnings] = useState(
    propErrorsAndWarnings.map((err) => ({
      ...err,
      dataType,
      isExpanded: true,
    })),
  );
  const [selectedItems, setSelectedItems] = useState(propSelectedItems);
  const [hasSelectedItems, setHasSelectedItems] = useState(propHasSelectedItems);
  const [continueIsDisabled, setContinueIsDisabled] = useState(
    !!propErrorsAndWarnings.find((err) => !err.isIgnorable) && continueCanBeDisabled,
  );

  const expandError = useCallback(
    (errorId) => {
      const updatedErrors = errorsAndWarnings.map((error) => ({
        ...error,
        isExpanded: error.id === errorId ? !error.isExpanded : error.isExpanded,
      }));
      setErrorsAndWarnings(updatedErrors);
    },
    [errorsAndWarnings, setErrorsAndWarnings],
  );

  const removeError = useCallback(
    (errorId) => {
      const matchingError = errorsAndWarnings.find((error) => error.id === errorId);
      if (!matchingError) {
        return;
      }
      const updates = removeItemsFromState(selectedItems, errorsAndWarnings, matchingError.data);
      setErrorsAndWarnings(updates.errorsAndWarnings);
      setSelectedItems(updates.selectedItems);
      setHasSelectedItems(!!updates.selectedItems.length);
      if (continueCanBeDisabled) {
        setContinueIsDisabled(updates.errorsAndWarnings.filter((error) => !error.isIgnorable).length > 0);
      }
    },
    [
      errorsAndWarnings,
      setErrorsAndWarnings,
      selectedItems,
      setSelectedItems,
      setHasSelectedItems,
      setContinueIsDisabled,
      continueCanBeDisabled,
    ],
  );

  const removeItemFromSelection = useCallback(
    (item) => {
      const updates = removeItemsFromState(selectedItems, errorsAndWarnings, [item]);
      setErrorsAndWarnings(updates.errorsAndWarnings);
      setSelectedItems(updates.selectedItems);
      setHasSelectedItems(!!updates.selectedItems.length);
      if (continueCanBeDisabled) {
        setContinueIsDisabled(updates.errorsAndWarnings.filter((error) => !error.isIgnorable).length > 0);
      }
      // dispatched for analytics only
      dispatch(removeItemFromSelectionAction(item));
    },
    [
      selectedItems,
      setSelectedItems,
      errorsAndWarnings,
      setErrorsAndWarnings,
      setHasSelectedItems,
      setContinueIsDisabled,
      continueCanBeDisabled,
      dispatch,
    ],
  );

  const onOrderNumberClick = useCallback(
    ({ salesOrderId }) => {
      dispatch(
        goToOrderDetails({
          salesOrderId,
          selectOrderInGrid: false,
        }),
      );
    },
    [dispatch],
  );

  const onShipmentNumberClick = useCallback(
    (item) => {
      dispatch(launchShipmentDetail(item.fulfillmentPlanId, dispatch));
    },
    [dispatch],
  );

  const additionalAction = isBatchPrinting
    ? {
        text: {
          id: 'test',
          defaultMessage: 'Skip Previously Printed',
        },
        handler: removeError,
      }
    : undefined;
  const { closeModal } = modalFrameworkMethods;
  const closeAndCancel = (...args) => {
    closeModal(args);
  };
  return (
    <Modal className={style['bulk-errors-and-warning-modal']} size='medium'>
      <ModalHeader closeModal={closeAndCancel}>{title && <FormattedMessage {...title} />}</ModalHeader>
      <ModalBody className={style['bulk-errors-and-warning-modal-body']}>
        {hasSelectedItems && errorsAndWarnings.length === 0 && (
          <div className={style['no-errors-message']}>
            <div>
              <FormattedMessage
                defaultMessage='All Errors have been resolved.'
                description='bulkErrorsAndWarningsModal.noErrors1'
                id='B5YWuO'
              />
            </div>
            <div>
              <FormattedMessage defaultMessage='You may continue.' description='bulkErrorsAndWarningsModal.noErrors2' id='0jjNMl' />
            </div>
          </div>
        )}
        {!hasSelectedItems && (
          <div className={style['no-errors-message']}>
            <div>
              <FormattedMessage
                defaultMessage='Everything has been removed.'
                description='bulkErrorsAndWarningsModal.noItems'
                id='h+GWU5'
              />
            </div>
            <div>
              <FormattedMessage
                defaultMessage='Please make a new selection.'
                description='bulkErrorsAndWarningsModal.noItems2'
                id='dJLKHh'
              />
            </div>
          </div>
        )}
        {hasSelectedItems &&
          errorsAndWarnings.length > 0 &&
          errorsAndWarnings.map((item) => (
            <BulkErrorOrWarning
              {...item}
              key={item.id}
              additionalAction={additionalAction}
              className={style['error-or-warning']}
              dataType={dataType}
              expandError={expandError}
              onOrderNumberClick={onOrderNumberClick}
              onShipmentNumberClick={onShipmentNumberClick}
              removeError={removeError}
              removeItemFromSelection={removeItemFromSelection}
            />
          ))}
      </ModalBody>
      <ModalFooter className={style['bulk-errors-and-warning-modal-footer']}>
        <Button onClick={closeAndCancel} testId='close-button-footer' theme='tertiary'>
          <FormattedMessage {...cancelButtonName} />
        </Button>
        {hasSelectedItems && (
          <Button
            disabled={continueIsDisabled}
            onClick={() => {
              onContinue({ errorsAndWarnings, selectedItems });
              closeModal();
            }}
            testId='continue-button'
            theme='primary'
          >
            <FormattedMessage defaultMessage='Continue' description='bulkErrorsAndWarningsModal.continue' id='pNR3qO' />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

BulkErrorsAndWarningsModal.propTypes = {
  title: MessageDescriptorPropType.isRequired,
  cancelButtonName: MessageDescriptorPropType,
  errorsAndWarnings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.oneOf(['error', 'warning']),
      message: PropTypes.node,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          salesOrderId: PropTypes.string,
          salesOrderNumber: PropTypes.string,
          fulfillmentPlanId: PropTypes.string,
          error: PropTypes.shape({
            text: PropTypes.string,
            handler: PropTypes.func,
          }),
        }),
      ),
    }),
  ),
  selectedItems: PropTypes.array.isRequired,
  onContinue: PropTypes.func,
  hasSelectedItems: PropTypes.bool,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  dataType: PropTypes.oneOf(['salesOrder', 'fulfillmentPlan', 'fulfillmentPlanWithoutSalesOrder', 'batchedFulfillmentPlan']).isRequired,
  isBatchPrinting: PropTypes.bool,
  continueCanBeDisabled: PropTypes.bool,
};
